// ===========================================
// Avatar Hooks
// ===========================================
// React Query hooks for user avatar features (Priority 11)
//
// IMPORTANT: This is for USER photo uploads (Admin, Teacher, Parent).
// Students have a SEPARATE avatar system using character sprites.
// ===========================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMyAvatar,
  uploadMyAvatar,
  removeMyAvatar,
  getUserAvatar,
  uploadUserAvatar,
  removeUserAvatar,
  getAvatarSettings,
  canUploadAvatar,
} from '../api/avatar.api';
import { UserAvatarInfo, AvatarSettings } from '../types/avatar.types';

// ===========================================
// QUERY KEY FACTORY
// ===========================================

export const avatarKeys = {
  all: ['avatar'] as const,
  // Current user avatar
  my: () => [...avatarKeys.all, 'me'] as const,
  canUpload: () => [...avatarKeys.all, 'can-upload'] as const,
  // User avatar by ID
  user: (userId: string) => [...avatarKeys.all, 'user', userId] as const,
  // Settings
  settings: () => [...avatarKeys.all, 'settings'] as const,
};

// ===========================================
// CURRENT USER AVATAR HOOKS
// ===========================================

/**
 * Get current user's avatar info
 */
export function useMyAvatar() {
  return useQuery({
    queryKey: avatarKeys.my(),
    queryFn: getMyAvatar,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Upload avatar for current user
 */
export function useUploadMyAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (file: File) => uploadMyAvatar(file),
    onSuccess: (data: UserAvatarInfo) => {
      // Update cache directly with the response data.
      // Do NOT invalidateQueries here - that triggers a background refetch
      // which can briefly overwrite the cache with stale server data
      // (e.g. if the server's file processing is slightly delayed).
      queryClient.setQueryData(avatarKeys.my(), data);
    },
  });
}

/**
 * Remove current user's avatar
 */
export function useRemoveMyAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => removeMyAvatar(),
    onSuccess: (data: UserAvatarInfo) => {
      // Update cache directly with the response data
      queryClient.setQueryData(avatarKeys.my(), data);
    },
  });
}

/**
 * Check if current user can upload an avatar
 */
export function useCanUploadAvatar() {
  return useQuery({
    queryKey: avatarKeys.canUpload(),
    queryFn: canUploadAvatar,
    staleTime: 30 * 60 * 1000, // 30 minutes (settings don't change often)
  });
}

// ===========================================
// USER AVATAR HOOKS (Admin/Teacher)
// ===========================================

/**
 * Get avatar info for a specific user
 * @param userId - User ID
 */
export function useUserAvatar(userId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: avatarKeys.user(userId),
    queryFn: () => getUserAvatar(userId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled !== false && !!userId,
  });
}

/**
 * Upload avatar for a specific user (admin only)
 */
export function useUploadUserAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, file }: { userId: string; file: File }) =>
      uploadUserAvatar(userId, file),
    onSuccess: (data: UserAvatarInfo, variables) => {
      // Update cache with new avatar info
      queryClient.setQueryData(avatarKeys.user(variables.userId), data);
      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: avatarKeys.user(variables.userId) });
    },
  });
}

/**
 * Remove avatar for a specific user (admin only)
 */
export function useRemoveUserAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeUserAvatar(userId),
    onSuccess: (data: UserAvatarInfo, userId) => {
      // Update cache with initials avatar
      queryClient.setQueryData(avatarKeys.user(userId), data);
      // Invalidate to ensure fresh data
      queryClient.invalidateQueries({ queryKey: avatarKeys.user(userId) });
    },
  });
}

// ===========================================
// AVATAR SETTINGS HOOKS
// ===========================================

/**
 * Get avatar settings for the school (admin only)
 */
export function useAvatarSettings() {
  return useQuery<AvatarSettings>({
    queryKey: avatarKeys.settings(),
    queryFn: getAvatarSettings,
    staleTime: 30 * 60 * 1000, // 30 minutes (settings don't change often)
  });
}

// ===========================================
// COMBINED HOOKS
// ===========================================

/**
 * Combined hook for avatar management in AccountSettings
 * Provides all avatar data and actions needed for the settings page
 */
export function useAvatarManagement() {
  const avatarQuery = useMyAvatar();
  const canUploadQuery = useCanUploadAvatar();
  const uploadMutation = useUploadMyAvatar();
  const removeMutation = useRemoveMyAvatar();

  return {
    // Data
    avatar: avatarQuery.data,
    canUpload: canUploadQuery.data ?? false,
    // Loading states
    isLoading: avatarQuery.isLoading || canUploadQuery.isLoading,
    isUploading: uploadMutation.isPending,
    isRemoving: removeMutation.isPending,
    // Error states
    error: avatarQuery.error || canUploadQuery.error,
    uploadError: uploadMutation.error,
    removeError: removeMutation.error,
    // Actions
    upload: uploadMutation.mutateAsync,
    remove: removeMutation.mutateAsync,
    // Refetch
    refetch: () => {
      avatarQuery.refetch();
      canUploadQuery.refetch();
    },
  };
}

/**
 * Combined hook for admin avatar management
 * Provides avatar data and actions for managing another user's avatar
 */
export function useAdminAvatarManagement(userId: string) {
  const avatarQuery = useUserAvatar(userId);
  const settingsQuery = useAvatarSettings();
  const uploadMutation = useUploadUserAvatar();
  const removeMutation = useRemoveUserAvatar();

  return {
    // Data
    avatar: avatarQuery.data,
    settings: settingsQuery.data,
    // Loading states
    isLoading: avatarQuery.isLoading || settingsQuery.isLoading,
    isUploading: uploadMutation.isPending,
    isRemoving: removeMutation.isPending,
    // Error states
    error: avatarQuery.error || settingsQuery.error,
    uploadError: uploadMutation.error,
    removeError: removeMutation.error,
    // Actions
    upload: (file: File) => uploadMutation.mutateAsync({ userId, file }),
    remove: () => removeMutation.mutateAsync(userId),
    // Refetch
    refetch: () => {
      avatarQuery.refetch();
      settingsQuery.refetch();
    },
  };
}
